// ═══════════════════════════════════════════════════════════════════════════
// Autorizar precios — la cola de quien decide
//
// Vive en la pestaña de Vendedores porque es ahí donde el dueño mira lo que
// hace su equipo: quién atiende a quién, cuánto se le debe y —ahora— qué está
// pidiendo. Ponerlo en Config lo habría escondido en la pantalla de las
// llaves, que es donde no se entra a diario.
//
// LO QUE UN RENGLÓN TIENE QUE DECIR SIN ABRIR NADA: quién pide, para qué
// cliente, qué producto, cuánto paga hoy, cuánto pide, y por qué. Con eso se
// decide en diez segundos. Sin eso, la cola se atora — y una cola atorada
// devuelve al vendedor a la llamada por teléfono, que es lo que veníamos a
// quitar.
//
// Rechazar PIDE una razón. Autorizar no la necesita: el sí se explica solo,
// el no es el que el vendedor va a preguntar.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import { solicitudes, resolverPrecio, ESTADO_SOLICITUD } from "../datos/precios-solicitud.js";
import { fmt } from "../pdf/comunes.js";
import { Card } from "./basicos.jsx";
import { btnPrimary, btnSecondary, inputStyle } from "./estilos.js";

export function AutorizarPrecios({ membresia }) {
  const tenantId = membresia?.tenant_id;
  const [lista, setLista] = useState([]);
  const [verTodo, setVerTodo] = useState(false);
  const [error, setError] = useState("");
  const [ocupada, setOcupada] = useState(null);      // id en curso
  const [rechazando, setRechazando] = useState(null); // id al que se le pide razón
  const [razon, setRazon] = useState("");

  const cargar = useCallback(async () => {
    if (!tenantId) return;
    try { setLista(await solicitudes(tenantId)); }
    catch (e) { setError(e.message); }
  }, [tenantId]);

  useEffect(() => { cargar(); }, [cargar]);

  const resolver = async (id, autorizar, nota) => {
    setOcupada(id); setError("");
    try {
      await resolverPrecio(id, autorizar, nota);
      setRechazando(null); setRazon("");
      await cargar();
    } catch (e) { setError(e.message); }
    setOcupada(null);
  };

  const pendientes = lista.filter(s => s.estado === "pendiente");
  const resueltas = lista.filter(s => s.estado !== "pendiente");
  const mostrar = verTodo ? lista : pendientes;

  // Callada cuando no hay nada. Una tarjeta que solo dice "no hay solicitudes"
  // ocupa el lugar de algo que sí importa.
  if (!pendientes.length && !resueltas.length) return null;

  return (
    <Card style={{ marginBottom: 20, borderColor: pendientes.length ? "#3a3a1a" : "#1e1e1e" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                    marginBottom: 4, gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, color: "#70c9c9", letterSpacing: 2 }}>
          ✋ PRECIOS POR AUTORIZAR
          {pendientes.length > 0 && (
            <span style={{ marginLeft: 8, color: "#facc15" }}>
              {pendientes.length} {pendientes.length === 1 ? "espera" : "esperan"} respuesta
            </span>
          )}
        </div>
        {resueltas.length > 0 && (
          <button onClick={() => setVerTodo(v => !v)}
            style={{ background: "transparent", border: "1px solid #222", borderRadius: 5,
                     color: "#888", fontSize: 10, padding: "3px 9px", cursor: "pointer" }}>
            {verTodo ? "solo pendientes" : `ver las ${resueltas.length} ya resueltas`}
          </button>
        )}
      </div>
      <div style={{ fontSize: 11, color: "#666", marginBottom: 14, lineHeight: 1.7 }}>
        Autorizar <b>aplica el precio</b> al cliente en ese momento. No hay que capturarlo
        aparte. Nada se borra: lo rechazado también se queda, con su razón.
      </div>

      {error && (
        <div style={{ background: "#1f0d0d", border: "1px solid #3a1a1a", borderRadius: 6,
                      padding: "10px 12px", marginBottom: 12, fontSize: 12, color: "#e8b0b0" }}>
          ⚠️ {error}
        </div>
      )}

      {mostrar.length === 0 && (
        <div style={{ fontSize: 12, color: "#555" }}>Ninguna espera respuesta.</div>
      )}

      {mostrar.map(s => {
        const e = ESTADO_SOLICITUD[s.estado] || ESTADO_SOLICITUD.pendiente;
        const pct = s.tipo === "descuento_cliente";
        const v = (n) => pct ? `${Number(n)}%` : fmt(n);
        const baja = Number(s.valor_pedido) < Number(s.valor_actual);
        return (
          <div key={s.id} style={{ borderTop: "1px solid #1a1a1a", padding: "12px 0" }}>
            <div style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 190 }}>
                <div style={{ fontSize: 14, color: "#e8e0d4" }}>
                  {s.producto || "Descuento del cliente"}
                </div>
                <div style={{ fontSize: 11, color: "#666" }}>
                  {s.cliente} · lo pide {s.pidio || "—"} ·{" "}
                  {new Date(s.created_at).toLocaleDateString("es-MX")}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                <span style={{ fontSize: 13, color: "#666" }}>{v(s.valor_actual)}</span>
                <span style={{ color: "#444" }}>→</span>
                <span style={{ fontSize: 16, fontWeight: 600,
                               color: pct ? (baja ? "#a8d060" : "#f0a050")
                                          : (baja ? "#f0a050" : "#a8d060") }}>
                  {v(s.valor_pedido)}
                </span>
              </div>

              {s.estado !== "pendiente" && (
                <span style={{ fontSize: 10, color: e.color, minWidth: 70, textAlign: "right" }}>
                  {e.etiqueta}
                </span>
              )}
            </div>

            {s.motivo && (
              <div style={{ fontSize: 12, color: "#888", marginTop: 6,
                            borderLeft: "2px solid #222", paddingLeft: 10 }}>
                {s.motivo}
              </div>
            )}

            {s.estado !== "pendiente" && (s.nota_decision || s.decidio) && (
              <div style={{ fontSize: 11, color: "#666", marginTop: 6 }}>
                {s.decidio}{s.decided_at ? ` · ${new Date(s.decided_at).toLocaleDateString("es-MX")}` : ""}
                {s.nota_decision ? ` — ${s.nota_decision}` : ""}
              </div>
            )}

            {s.estado === "pendiente" && rechazando !== s.id && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 10, justifyContent: "flex-end" }}>
                <button onClick={() => { setRechazando(s.id); setRazon(""); }}
                  disabled={ocupada === s.id}
                  style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12,
                           borderColor: "#3a2020", color: "#c97070" }}>
                  Rechazar
                </button>
                <button onClick={() => resolver(s.id, true, null)} disabled={ocupada === s.id}
                  style={{ ...btnPrimary, padding: "6px 16px", fontSize: 12,
                           background: "linear-gradient(135deg, #1a3a1a, #0d2a0d)",
                           borderColor: "#2a5a2a", color: "#4ade80",
                           opacity: ocupada === s.id ? 0.5 : 1 }}>
                  {ocupada === s.id ? "Aplicando…" : "Autorizar y aplicar"}
                </button>
              </div>
            )}

            {rechazando === s.id && (
              <div style={{ marginTop: 10 }}>
                <input value={razon} onChange={ev => setRazon(ev.target.value)} autoFocus
                  placeholder="Por qué no — lo va a leer quien lo pidió"
                  style={{ ...inputStyle, marginBottom: 8 }} />
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "flex-end" }}>
                  <button onClick={() => { setRechazando(null); setRazon(""); }}
                    style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12 }}>
                    Mejor no
                  </button>
                  <button onClick={() => resolver(s.id, false, razon)}
                    disabled={!razon.trim() || ocupada === s.id}
                    style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12,
                             borderColor: "#3a2020", color: "#c97070",
                             opacity: razon.trim() ? 1 : 0.4 }}>
                    {ocupada === s.id ? "…" : "Rechazar"}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </Card>
  );
}
